import { jsx, jsxs } from 'react/jsx-runtime';
import { ErrorMessage } from '@promo-platform/error-message';
import { FieldHead } from '@promo-platform/field-head';
import { useConditionsWatch } from '@promo-platform/utils';
import classNames from 'classnames';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

function __rest(s, e) {
  var t = {};

  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}
function __awaiter(thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }

  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }

    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }

    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }

    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
}

const RemoveIcon = (_a) => {
    var htmlProperties = __rest(_a, []);
    return (jsx("svg", Object.assign({}, htmlProperties, { xmlns: "http://www.w3.org/2000/svg", width: "31.113", height: "31.113" }, { children: jsxs("g", Object.assign({ fill: "none", stroke: "#ff5478", strokeLinecap: "round", strokeWidth: "2" }, { children: [jsx("path", { "data-name": "Line 2", d: "M23.335 7.779 7.778 23.335" }), jsx("path", { "data-name": "Line 3", d: "M23.335 23.335 7.778 7.78" })] })) })));
};

const ThumbnailIcon = (_a) => {
    var htmlProperties = __rest(_a, []);
    return (jsx("svg", Object.assign({}, htmlProperties, { xmlns: "http://www.w3.org/2000/svg", width: "52", height: "38" }, { children: jsxs("g", Object.assign({ transform: "translate(-3.625 -3.789)", fill: "none", stroke: "#939fb0", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2" }, { children: [jsx("rect", { width: "50", height: "36", rx: "5", transform: "translate(4.625 4.789)" }), jsx("path", { d: "m10.951 35.676 8.3-9.709 6.455 5.178L36.77 20.789l12.91 14.887Z" }), jsx("circle", { cx: "4.5", cy: "4.5", r: "4.5", transform: "translate(20.625 10.789)" })] })) })));
};

const kb = 1024;
const convertToMB = (size) => parseFloat((size / (kb * kb)).toFixed(2));
const convertBase64 = (file) => new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onload = () => {
        resolve(fileReader.result);
    };
    fileReader.onerror = (error) => {
        reject(error);
    };
});
const base64Handler = (files) => __awaiter(void 0, void 0, void 0, function* () {
    const filePathsPromises = files.map((file) => convertBase64(file));
    const filePaths = yield Promise.all(filePathsPromises);
    return filePaths.map((base64File, index) => ({
        value: base64File,
        name: files[index].name,
        size: files[index].size,
        type: files[index].type,
    }));
});

const AttachFieldField = ({ alias, id, label, description, tooltip, tooltipLabel, disabled, acceptedFileTypesValidation, maxFileSizeValidation, maxNumberOfFilesValidation, minNumberOfFilesValidation, }) => {
    var _a;
    const { register, getValues, setValue, setError, trigger, formState: { errors, dirtyFields }, } = useFormContext();
    const { [alias]: addedFiles } = getValues();
    useEffect(() => {
        if (dirtyFields[alias]) {
            trigger(alias);
        }
    }, [addedFiles, dirtyFields, trigger, alias]);
    const handleFiles = (files) => __awaiter(void 0, void 0, void 0, function* () {
        if (!(files === null || files === void 0 ? void 0 : files[0])) {
            return;
        }
        if (acceptedFileTypesValidation) {
            const extensions = acceptedFileTypesValidation.acceptedFileTypes.map((item) => item.toLowerCase());
            if (Array.from(files).some(({ name }) => !extensions.includes(name.split('.').at(-1).toLowerCase()))) {
                setError(alias, {
                    type: 'acceptedTypesOfFile',
                    message: acceptedFileTypesValidation.errorMessage,
                });
                return;
            }
        }
        if (minNumberOfFilesValidation &&
            Array.from(files).length < minNumberOfFilesValidation.value) {
            setError(alias, {
                type: 'minNumberOfFile',
                message: minNumberOfFilesValidation.errorMessage,
            });
            return;
        }
        if (maxNumberOfFilesValidation &&
            Array.from(files).length > maxNumberOfFilesValidation.value) {
            setError(alias, {
                type: 'maxNumberOfFile',
                message: maxNumberOfFilesValidation.errorMessage,
            });
            return;
        }
        const isSizeValid = !Array.from(files).some((file) => convertToMB(file.size) > (maxFileSizeValidation === null || maxFileSizeValidation === void 0 ? void 0 : maxFileSizeValidation.value) || 0);
        const filesWithBase64 = yield base64Handler(Array.from(files));
        if (isSizeValid) {
            setValue(alias, filesWithBase64, {
                shouldValidate: true,
                shouldDirty: true,
            });
        }
        else {
            setError(alias, {
                type: 'maxSize',
                message: maxFileSizeValidation.errorMessage,
            });
        }
    });
    const onChange = (event) => {
        const { files } = event.target;
        handleFiles(files);
    };
    const onDrop = (event) => {
        if (event.dataTransfer) {
            const { files } = event.dataTransfer;
            handleFiles(files);
        }
    };
    const removeFile = (name) => {
        setValue(alias, addedFiles === null || addedFiles === void 0 ? void 0 : addedFiles.filter((file) => file.name !== name), {
            shouldValidate: true,
        });
    };
    const { ref } = register(alias, {
        required: minNumberOfFilesValidation &&
            (addedFiles === null || addedFiles === void 0 ? void 0 : addedFiles.length) < minNumberOfFilesValidation.value &&
            minNumberOfFilesValidation.errorMessage,
    });
    return (jsxs("div", Object.assign({ className: "pp-attach-field" }, { children: [jsx(FieldHead, { id: id, label: label, description: description, tooltipText: tooltip, tooltipLabel: tooltipLabel }), jsx("input", { ref: ref, className: "pp-attach-field__file-input", id: `pp-${alias}`, accept: acceptedFileTypesValidation.acceptedFileTypes
                    .map((item) => `.${item.toLowerCase()}`)
                    .join(','), "data-test": "attach-field", type: "file", onChange: onChange, name: alias, multiple: maxNumberOfFilesValidation && maxNumberOfFilesValidation.value > 1 }), jsxs("label", Object.assign({ htmlFor: `pp-${alias}`, onDrop: onDrop, className: classNames('pp-attach-field__label', {
                    'pp-attach-field__label--disabled': disabled,
                    'pp-attach-field__label--error': Boolean(errors === null || errors === void 0 ? void 0 : errors[alias]),
                }) }, { children: [jsx("span", { className: classNames('pp-attach-field__button', {
                            'pp-attach-field__button--disabled': disabled,
                        }), role: "button" }), jsx("span", Object.assign({ className: "pp-attach-field__info" }, { children: "Browse your files" }))] })), (addedFiles === null || addedFiles === void 0 ? void 0 : addedFiles.length) > 0
                ? addedFiles.map((file) => (jsxs("div", Object.assign({ className: "pp-attach-field__file" }, { children: [jsx(ThumbnailIcon, {}), jsxs("div", Object.assign({ className: "pp-attach-field__infoContainer" }, { children: [jsx("div", Object.assign({ className: "pp-attach-field__name" }, { children: file.name })), jsxs("div", Object.assign({ className: "pp-attach-field__size" }, { children: [convertToMB(file.size), " mb"] }))] })), jsx("button", Object.assign({ type: "button", className: "pp-attach-field__removeButton", onClick: () => removeFile(file.name) }, { children: jsx(RemoveIcon, {}) }))] }), file.name)))
                : null, jsx(ErrorMessage, { message: (_a = errors[alias]) === null || _a === void 0 ? void 0 : _a.message })] })));
};
const AttachField = (props) => {
    const { unregister, watch } = useFormContext();
    const { alias, conditions } = props;
    const shouldRender = useConditionsWatch(watch, conditions);
    useEffect(() => {
        if (!shouldRender) {
            unregister(alias);
        }
    }, [shouldRender, unregister, alias]);
    return shouldRender ? jsx(AttachFieldField, Object.assign({}, props)) : null;
};

export { AttachField };
